import React, { useLayoutEffect, useRef, useState } from "react";
import InvoicePreview from "./InvoicePreview";
import MealPlanPreview from "./MealPlanPreview";
import { PAGE_HEIGHT, PAGE_WIDTH } from "../previewPage";
import { BrandDetails, BrandPreviewId } from "../types/brand.types";

/**
 * The page size the documents are laid out at lives in `previewPage`, beside
 * the margins printed on it, and is re-exported here because the two things
 * that draw a document — the column beside the form and the sheet the narrow
 * screens open — size their own measuring box from it.
 */
export { PAGE_HEIGHT, PAGE_WIDTH };

/** The two documents a brand produces, in the order they are offered. */
export const PREVIEW_DOCUMENTS: Array<{ id: BrandPreviewId; label: string }> = [
  { id: "meal-plan", label: "Meal plan" },
  { id: "invoice", label: "Invoice" },
];

/**
 * What the frame spends on itself around the page: the stack's scrollbar,
 * which stands beside the sheets rather than over them, and its own border
 * either side. Reserved whether or not the open document has more than one
 * sheet, so switching to the meal plan does not move the frame.
 */
const SCROLLBAR = 8;
export const FRAME_BORDER = 2;
export const FRAME_CHROME = SCROLLBAR + FRAME_BORDER;

/**
 * The footprint a frame takes at `scale` — one whole page and the chrome
 * around it, whatever is in it. The block that stands in while a document is
 * still coming takes the same, so the page lands in the space its own
 * placeholder held.
 */
export const frameFootprint = (scale: number) => ({
  width: PAGE_WIDTH * scale + FRAME_CHROME,
  height: PAGE_HEIGHT * scale + FRAME_BORDER,
});

interface BrandDocumentProps {
  id: BrandPreviewId;
  details: BrandDetails;
  /** What the page is drawn at, as a fraction of `PAGE_WIDTH`. */
  scale: number;
}

/**
 * One of the two documents, drawn at the canonical page size and scaled into
 * the space the caller has.
 *
 * The frame holds exactly one page however many the document runs to: the meal
 * plan is a stack — the cover, a sheet per meal, and the closing Guidelines and
 * Policy — and is turned inside the frame rather than making the column it
 * sits in taller. The invoice is one sheet and never scrolls.
 *
 * Three boxes rather than one. The outer is the frame and takes the footprint
 * above, so the layout around it reserves the size the page ends up. The
 * middle is what the document takes once it is scaled, and is the only thing
 * the frame's scrollbar measures: a transform moves no layout, so without it
 * the stack is scrolled as though it were still full size and a band of
 * nothing follows the last sheet. The inner keeps the page's own dimensions
 * and is transformed.
 */
const BrandDocument: React.FC<BrandDocumentProps> = ({
  id,
  details,
  scale,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);

  /** The open document's own height, before the transform below shrinks it. */
  const [documentHeight, setDocumentHeight] = useState(PAGE_HEIGHT);

  const multipage = id === "meal-plan";

  // How tall the open document is — one sheet for the invoice, the whole stack
  // for the meal plan, which is not known until its closing documents have
  // been split into sheets. Read off the layout, which a transform does not
  // touch.
  useLayoutEffect(() => {
    const stack = documentRef.current;
    if (!stack) return;

    const read = () => setDocumentHeight(stack.offsetHeight || PAGE_HEIGHT);

    read();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    observer?.observe(stack);
    return () => observer?.disconnect();
  }, [id]);

  return (
    <div
      // The overflow is set here rather than in a class: the scrollbar's own
      // rule sets the shorthand, and an `overflow-x` utility beside it would
      // be at the mercy of which stylesheet came last.
      style={{
        ...frameFootprint(scale),
        overflowX: "hidden",
        overflowY: multipage ? "auto" : "hidden",
      }}
      className="card-surface border border-line bg-white custom-scrollbar-overlay"
    >
      <div style={{ height: documentHeight * scale, overflow: "hidden" }}>
        <div
          ref={documentRef}
          style={{
            width: PAGE_WIDTH,
            // The meal plan is as tall as its own stack of sheets. The invoice
            // is one sheet and is given its height, because the page inside it
            // fills whatever it is put in.
            height: multipage ? undefined : PAGE_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {id === "invoice" ? (
            <InvoicePreview details={details} />
          ) : (
            <MealPlanPreview details={details} />
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandDocument;
